import {
  ClickableToastStack,
  formatDateTime,
  NOTIFICATION_SOUND_KEY,
  NotificationBell,
  type NotificationItem,
  newArrivals,
  playChime,
  readFlag,
} from "@majitap/ui";
import { useToastStack } from "@majitap/ui/arc/toast-stack/toast-stack";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { errorOf } from "../api";
import { useCompanyFormat } from "../company";
import { arrivalsQuery, inboxQuery, markAllRead, markRead, refreshNotifications, unreadQuery } from "../notifications";

/** The bell in the console's top bar (P11-03): staff alerts and anything else sent to me. */
export function ConsoleNotifications() {
  const queryClient = useQueryClient();
  const format = useCompanyFormat();
  const [open, setOpen] = useState(false);
  const unread = useQuery(unreadQuery);
  const inbox = useQuery({ ...inboxQuery, enabled: open });
  return (
    <NotificationBell
      unread={unread.data?.unread ?? 0}
      items={inbox.data?.data}
      error={inbox.error ? errorOf(inbox.error).message : undefined}
      formatTime={(iso) => formatDateTime(iso, format)}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) void refreshNotifications(queryClient);
      }}
      onSelect={(item) => {
        if (item.read_at) return;
        void markRead(item.id).then(() => refreshNotifications(queryClient));
      }}
      onReadAll={() => markAllRead().then(() => refreshNotifications(queryClient))}
    />
  );
}

/** At most this many toasts for one check; the rest are summed up in one more. */
const TOASTS_AT_ONCE = 3;

/**
 * New notifications as they arrive, as toasts at the bottom right, with a chime unless it's
 * switched off on Account. Clicking one opens it, as the bell does: its link, and it's read. What
 * was already there when the console opened isn't toasted.
 */
export function NotificationToasts() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { toast, dismiss } = useToastStack();
  const arrivals = useQuery(arrivalsQuery);
  const seen = useRef<Set<string> | null>(null);
  // what's toasted, newest last: a click is matched back by the toast's text
  const toasted = useRef<NotificationItem[]>([]);
  useEffect(() => {
    const items = arrivals.data?.data;
    if (!items) return;
    if (!seen.current) {
      seen.current = new Set(items.map((item) => item.id));
      return;
    }
    const fresh = newArrivals(items, seen.current);
    if (!fresh.length) return;
    void queryClient.invalidateQueries({ queryKey: unreadQuery.queryKey });
    for (const item of fresh.slice(-TOASTS_AT_ONCE)) {
      toasted.current = [...toasted.current.slice(-20), item];
      toast({ id: item.id, title: item.title, description: item.body, duration: 8000 });
    }
    if (fresh.length > TOASTS_AT_ONCE) {
      const more = fresh.length - TOASTS_AT_ONCE;
      toast({
        title: `${more} more new ${more === 1 ? "notification" : "notifications"}`,
        description: "Under the bell.",
      });
    }
    if (readFlag(NOTIFICATION_SOUND_KEY) !== false) playChime();
  }, [arrivals.data, queryClient, toast]);

  const open = (text: string) => {
    const item = toasted.current.findLast((n) => text.includes(n.title) && text.includes(n.body));
    if (!item) return;
    dismiss(item.id);
    void markRead(item.id).then(() => refreshNotifications(queryClient));
    if (item.link) void navigate({ href: item.link });
  };
  return <ClickableToastStack label="New notifications" onToastClick={open} />;
}
